import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext();

const API_BASE_URL = 'https://trcsite.net/api';
axios.defaults.baseURL = API_BASE_URL;

export function useAuth() {
  return useContext(AuthContext);
}

function clearSession() {
  localStorage.removeItem('token');
  localStorage.removeItem('user');
  delete axios.defaults.headers.common['Authorization'];
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const logout = useCallback(() => {
    clearSession();
    setUser(null);
  }, []);

  const applyToken = useCallback((token, userData) => {
    if (token) {
      localStorage.setItem('token', token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }
    if (userData) {
      localStorage.setItem('user', JSON.stringify(userData));
      setUser(userData);
    }
  }, []);

  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        const code = error.response?.data?.code;
        const status = error.response?.status;
        const url = error.config?.url || '';
        const isAuthAttempt = url.includes('/auth/login') || url.includes('/auth/register');

        if (!isAuthAttempt && (code === 'SESSION_REVOKED' || (status === 401 && localStorage.getItem('token')))) {
          if (code === 'SESSION_REVOKED' || url.includes('/auth/verify')) {
            clearSession();
            setUser(null);
          }
        }
        return Promise.reject(error);
      }
    );

    return () => axios.interceptors.response.eject(interceptor);
  }, []);

  const verifyToken = useCallback(async (isInitial = false) => {
    try {
      const token = localStorage.getItem('token');
      if (token) {
        const response = await axios.get('/auth/verify');
        const updatedUser = response.data.user;
        setUser(updatedUser);
        localStorage.setItem('user', JSON.stringify(updatedUser));
      }
    } catch (error) {
      if (isInitial || error.response?.data?.code === 'SESSION_REVOKED') {
        clearSession();
        setUser(null);
      }
    } finally {
      if (isInitial) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');

    if (token && savedUser) {
      try {
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        setUser(JSON.parse(savedUser));
        verifyToken(true);
      } catch (error) {
        clearSession();
        setLoading(false);
      }
    } else {
      setLoading(false);
    }
  }, [verifyToken]);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible' && localStorage.getItem('token')) {
        verifyToken(false);
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [verifyToken]);

  const login = async (email, password) => {
    try {
      const response = await axios.post('/auth/login', { email, password });
      const { token, user } = response.data;
      applyToken(token, user);
      return { success: true, message: 'Login successful!', user };
    } catch (error) {
      return { success: false, error: error.response?.data?.error || 'Login failed' };
    }
  };

  const register = async (name, email, password) => {
    try {
      const response = await axios.post('/auth/register', { name, email, password });
      const { token, user, message } = response.data;
      applyToken(token, user);
      return {
        success: true,
        message: message || `Registration successful! Your UID is: ${user.uid}`,
        user
      };
    } catch (error) {
      return { success: false, error: error.response?.data?.error || 'Registration failed' };
    }
  };

  const updateUser = (userData) => {
    const updatedUser = { ...user, ...userData };
    setUser(updatedUser);
    localStorage.setItem('user', JSON.stringify(updatedUser));
  };

  const refreshUser = async () => {
    try {
      const response = await axios.get('/auth/profile');
      updateUser(response.data.user);
      return { success: true };
    } catch (error) {
      return { success: false };
    }
  };

  const value = {
    user,
    login,
    register,
    logout,
    loading,
    updateUser,
    refreshUser,
    applyToken,
    isAuthenticated: () => !!user && !!localStorage.getItem('token'),
    hasRole: (role) => user?.role === role,
    isAdmin: () => user?.role === 'admin' || user?.role === 'moderator',
    isVerified: () => user?.is_verified === true,
    getKYCStatus: () => user?.kyc_status || 'pending',
    getUID: () => user?.uid || null
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
